/**
 * Phase 5Z.32 — Operations · Heute (modernized)
 *
 * Improvements over the previous version:
 *  - 8 granular KPI tiles (im Dienst, verspätet, fehlend, Urlaub, Krank, Frei,
 *    Fahrzeuge draussen, fällige Aufgaben). Click on a KPI scrolls to its panel.
 *  - Abteilungs-Filter (client-side) — Admin/GF + everyone with multi-firm
 *    access also see the FirmFilter alongside.
 *  - Quick-Actions per driver row: 📞 Anrufen, 💬 WhatsApp, 👤 Profil
 *  - Auto-Refresh-Toggle (60 s) + "zuletzt aktualisiert vor X s" indicator
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { StatusBadge, EmptyState } from "@/components/Shared";
import {
  Users, Phone, AlertCircle, Truck, Calendar, CheckSquare, RefreshCw,
  Clock, Sun, Stethoscope, Coffee, Home, MessageCircle, UserCircle2,
  PauseCircle, PlayCircle,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";
import FirmFilter from "@/components/FirmFilter";

const REFRESH_MS = 60_000;

function tidyPhone(p) {
  return (p || "").replace(/[\s/.-]/g, "");
}

export default function Heute() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState("");
  const [departments, setDepartments] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem("heute_auto_refresh") === "1"; } catch { return false; }
  });
  const [lastLoaded, setLastLoaded] = useState(null);
  const [tick, setTick] = useState(0);  // forces "vor X s" re-render
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/operations/today");
      setData(data);
      setLastLoaded(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + load departments for the filter
  useEffect(() => {
    load();
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
  }, [load]);

  // Auto-refresh interval
  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoRefresh) timerRef.current = setInterval(() => load(), REFRESH_MS);
    try { localStorage.setItem("heute_auto_refresh", autoRefresh ? "1" : "0"); } catch { /* ignore */ }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, load]);

  // Ticker for the "vor X s" label (cheap, 5 s granularity)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  // Filter rows client-side by department
  const filtered = useMemo(() => {
    if (!data) return null;
    if (!dept) return data;
    const f = (arr) => (arr || []).filter((r) =>
      (r.department_id && r.department_id === dept) ||
      (!r.department_id && r.department === dept)
    );
    return {
      ...data,
      drivers_today: f(data.drivers_today),
      missing: f(data.missing),
      verspaetet: f(data.verspaetet),
      urlaub: f(data.urlaub),
      krank: f(data.krank),
      frei: f(data.frei),
      homeoffice: f(data.homeoffice),
    };
  }, [data, dept]);

  if (loading && !data) return <div className="p-6 text-zinc-500 text-sm">Lade Tagesansicht...</div>;
  if (!data) return null;

  const today = new Date().toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const c = {
    drivers_working: filtered.drivers_today.length,
    missing: filtered.missing.length,
    verspaetet: filtered.verspaetet.length,
    urlaub: filtered.urlaub.length,
    krank: filtered.krank.length,
    frei: filtered.frei.length,
    homeoffice: filtered.homeoffice.length,
    vehicles_out: data.counts.vehicles_out,
    tasks_due: data.counts.tasks_due,
  };

  // "vor X s" / "vor X min" since last load
  const ageLabel = (() => {
    if (!lastLoaded) return "—";
    const sec = Math.round((Date.now() - lastLoaded.getTime()) / 1000);
    if (sec < 60) return `vor ${sec} s`;
    return `vor ${Math.round(sec / 60)} min`;
  })();
  void tick; // referenced just to make the linter happy

  function scrollTo(testid) {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="p-6 md:p-8" data-testid="heute-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Tagesansicht</div>
          <h2 className="font-display text-2xl font-bold text-zinc-50 mt-1">Heute · {today}</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Live-Status aller aktiven Mitarbeiter und Fahrzeuge · zuletzt {ageLabel}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAutoRefresh((a) => !a)}
            data-testid="heute-auto-refresh-toggle"
            title={autoRefresh ? "Auto-Refresh aus" : "Auto-Refresh ein (60 s)"}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded transition-colors ${
              autoRefresh
                ? "bg-emerald-600/20 border border-emerald-500/40 text-emerald-300"
                : "bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300"
            }`}
          >
            {autoRefresh ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
            {autoRefresh ? "Auto · 60 s" : "Auto aus"}
          </button>
          <button onClick={load} data-testid="heute-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm rounded">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 -mt-2 flex-wrap">
        <FirmFilter compact />
        {departments.length > 0 && (
          <select
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            data-testid="heute-dept-filter"
            className="px-2.5 py-1.5 text-xs bg-zinc-900 border border-zinc-800 rounded text-zinc-100 focus:outline-none focus:border-zinc-700"
          >
            <option value="">Alle Abteilungen</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        {dept && (
          <button onClick={() => setDept("")} data-testid="heute-dept-clear"
            className="text-[11px] text-zinc-500 hover:text-zinc-300">Filter leeren</button>
        )}
      </div>

      <ModuleHint moduleKey="heute" />

      {/* KPI tiles — 8 across, click → scroll to panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        <KPI label="Im Dienst" value={c.drivers_working} icon={Users} tone="success" onClick={() => scrollTo("panel-drivers")} testid="kpi-drivers" />
        <KPI label="Verspätet" value={c.verspaetet} icon={Clock} tone={c.verspaetet > 0 ? "danger" : "default"} onClick={() => scrollTo("panel-verspaetet")} testid="kpi-verspaetet" />
        <KPI label="Fehlend" value={c.missing} icon={AlertCircle} tone={c.missing > 0 ? "warning" : "default"} onClick={() => scrollTo("panel-missing")} testid="kpi-missing" />
        <KPI label="Urlaub" value={c.urlaub} icon={Sun} tone="info" onClick={() => scrollTo("panel-urlaub")} testid="kpi-urlaub" />
        <KPI label="Krank" value={c.krank} icon={Stethoscope} tone={c.krank > 0 ? "danger" : "default"} onClick={() => scrollTo("panel-krank")} testid="kpi-krank" />
        <KPI label="Frei" value={c.frei} icon={Coffee} tone="default" onClick={() => scrollTo("panel-frei")} testid="kpi-frei" />
        <KPI label="Fahrzeuge draußen" value={c.vehicles_out} icon={Truck} tone="default" onClick={() => scrollTo("panel-vehicles-out")} testid="kpi-vehicles-out" />
        <KPI label="Offene Aufgaben" value={c.tasks_due} icon={CheckSquare} tone={c.tasks_due > 0 ? "warning" : "default"} onClick={() => scrollTo("panel-tasks")} testid="kpi-tasks" />
      </div>

      {/* Panels grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Im Dienst" count={c.drivers_working} testid="panel-drivers">
          {filtered.drivers_today.length === 0 ? <EmptyState title="Niemand im Dienst" /> : (
            <div className="divide-y divide-zinc-800">
              {filtered.drivers_today.map((d) => (
                <PersonRow key={d.id} d={d} onProfile={() => navigate(`/mitarbeiter/${d.id}`)} variant="working" />
              ))}
            </div>
          )}
        </Panel>

        {c.verspaetet > 0 && (
          <Panel title="Verspätet" count={c.verspaetet} tone="danger" testid="panel-verspaetet">
            <div className="divide-y divide-zinc-800">
              {filtered.verspaetet.map((d) => (
                <PersonRow key={d.id} d={d} onProfile={() => navigate(`/mitarbeiter/${d.id}`)} variant="late" />
              ))}
            </div>
          </Panel>
        )}

        <Panel title="Fehlende Einträge" count={c.missing} tone={c.missing > 0 ? "warning" : "default"} testid="panel-missing">
          {filtered.missing.length === 0 ? <EmptyState title="Alle Einträge erfasst" /> : (
            <div className="divide-y divide-zinc-800">
              {filtered.missing.map((m) => (
                <PersonRow key={m.id} d={m} onProfile={() => navigate(`/mitarbeiter/${m.id}`)} variant="missing" />
              ))}
            </div>
          )}
        </Panel>

        {c.urlaub > 0 && (
          <Panel title="Urlaub" count={c.urlaub} tone="info" testid="panel-urlaub">
            <div className="divide-y divide-zinc-800">
              {filtered.urlaub.map((a) => <AbsentRow key={a.id} a={a} type="urlaub" />)}
            </div>
          </Panel>
        )}

        {c.krank > 0 && (
          <Panel title="Krank" count={c.krank} tone="danger" testid="panel-krank">
            <div className="divide-y divide-zinc-800">
              {filtered.krank.map((a) => <AbsentRow key={a.id} a={a} type="krank" />)}
            </div>
          </Panel>
        )}

        {c.frei > 0 && (
          <Panel title="Frei" count={c.frei} testid="panel-frei">
            <div className="divide-y divide-zinc-800">
              {filtered.frei.map((a) => <AbsentRow key={a.id} a={a} type="frei" />)}
            </div>
          </Panel>
        )}

        {c.homeoffice > 0 && (
          <Panel title="Homeoffice" count={c.homeoffice} testid="panel-homeoffice">
            <div className="divide-y divide-zinc-800">
              {filtered.homeoffice.map((a) => <AbsentRow key={a.id} a={a} type="homeoffice" />)}
            </div>
          </Panel>
        )}

        <Panel title="Fahrzeuge unterwegs" count={c.vehicles_out} testid="panel-vehicles-out">
          {data.vehicles_out.length === 0 ? <EmptyState title="Alle Fahrzeuge am Standort" /> : (
            <div className="divide-y divide-zinc-800">
              {data.vehicles_out.map((v) => (
                <div key={v.vehicle_id} className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-zinc-800/40 px-2 -mx-2 rounded" onClick={() => navigate(`/fuhrpark/${v.vehicle_id}`)}>
                  <div className="font-mono text-sm text-zinc-100">{v.plate}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-400 truncate">{v.model}</div>
                    <div className="text-xs text-zinc-500">Fahrer: {v.driver_name} · seit {v.since}</div>
                  </div>
                  {v.km_at_handover && <div className="text-xs font-mono text-zinc-500">{v.km_at_handover.toLocaleString("de-AT")} km</div>}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Heutige Übergaben" count={data.recent_handovers.length} testid="panel-handovers">
          {data.recent_handovers.length === 0 ? <EmptyState title="Keine Übergaben heute" /> : (
            <div className="divide-y divide-zinc-800">
              {data.recent_handovers.map((h, i) => (
                <div key={`${h.plate}-${h.driver}-${i}`} className="py-2.5 flex items-center gap-3">
                  <StatusBadge status={h.direction === "uebergabe" ? "ok" : "bald"} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100">{h.direction === "uebergabe" ? "Übergabe" : "Rücknahme"} · <span className="font-mono">{h.plate}</span></div>
                    <div className="text-xs text-zinc-500">{h.driver} · {h.km?.toLocaleString("de-AT")} km · Tank {h.fuel_percent}%</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Fällige Aufgaben" count={c.tasks_due} tone={c.tasks_due > 0 ? "warning" : "default"} testid="panel-tasks">
          {data.tasks_due.length === 0 ? <EmptyState title="Keine fälligen Aufgaben" /> : (
            <div className="divide-y divide-zinc-800">
              {data.tasks_due.map((t) => (
                <div key={t.id} className="py-2.5 flex items-center gap-3">
                  <div className="w-1 h-8 rounded-full bg-amber-400"></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                    <div className="text-xs text-zinc-500">{t.assigned_to || "—"} · fällig: {t.due_date || "—"}</div>
                  </div>
                  <StatusBadge status={t.priority} />
                </div>
              ))}
              <button onClick={() => navigate("/operations/aufgaben")} className="mt-2 w-full text-xs text-blue-400 hover:text-blue-300 py-1.5">Alle Aufgaben öffnen →</button>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

function KPI({ label, value, icon: Icon, tone, onClick, testid }) {
  const colors = {
    default: "text-zinc-100 border-zinc-800",
    success: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    warning: "text-amber-400 border-amber-500/30 bg-amber-500/5",
    danger:  "text-rose-400 border-rose-500/30 bg-rose-500/5",
    info:    "text-sky-400 border-sky-500/30 bg-sky-500/5",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`bg-zinc-900 border rounded-md p-3 text-left hover:border-zinc-600 transition-colors ${colors[tone] || colors.default}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5 opacity-60" />
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-semibold truncate">{label}</span>
      </div>
      <div className="text-2xl font-mono font-bold tabular-nums">{value}</div>
    </button>
  );
}

function Panel({ title, count, children, tone, testid }) {
  const colors = { warning: "border-amber-500/20", danger: "border-rose-500/30", info: "border-sky-500/20", default: "border-zinc-800" };
  return (
    <div className={`bg-zinc-900 border rounded-md p-5 ${colors[tone] || colors.default}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-zinc-800">
        <h3 className="font-display font-semibold text-zinc-100">{title}</h3>
        <span className="text-xs font-mono tabular-nums text-zinc-500 px-2 py-0.5 bg-zinc-800 rounded">{count}</span>
      </div>
      {children}
    </div>
  );
}

function PersonRow({ d, onProfile, variant }) {
  const tel = tidyPhone(d.phone);
  const variantStyles = {
    working: { dot: "bg-emerald-500/20 text-emerald-400", icon: Users },
    missing: { dot: "bg-amber-500/20 text-amber-400", icon: AlertCircle },
    late:    { dot: "bg-rose-500/20 text-rose-400", icon: Clock },
  }[variant] || { dot: "bg-zinc-700 text-zinc-300", icon: UserCircle2 };

  return (
    <div className="py-2.5 flex items-center gap-3 hover:bg-zinc-800/40 px-2 -mx-2 rounded group">
      <button onClick={onProfile} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${variantStyles.dot}`} title="Profil öffnen">
        {d.name?.split(" ").map((n) => n[0]).slice(0, 2).join("") || "?"}
      </button>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onProfile}>
        <div className="text-sm text-zinc-100 font-medium truncate">{d.name}</div>
        <div className="text-xs text-zinc-500 truncate">
          {variant === "working" && d.work_start && <span className="font-mono">{d.work_start}–{d.work_end || "…"} · </span>}
          {variant === "late" && d.expected_start && <span className="text-rose-300 font-mono">geplant: {d.expected_start} · </span>}
          {d.role || "—"} · {d.department || "—"}
          {variant === "working" && d.km_driven > 0 && <span className="font-mono"> · {d.km_driven.toLocaleString("de-AT")} km</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {tel && (
          <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()} title={`Anrufen: ${d.phone}`}
             className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" data-testid={`heute-call-${d.id}`}>
            <Phone className="w-3.5 h-3.5" />
          </a>
        )}
        {tel && (
          <a href={`https://wa.me/${tel.replace(/^\+/, "")}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="WhatsApp"
             className="p-1.5 rounded hover:bg-zinc-700 text-emerald-400" data-testid={`heute-wa-${d.id}`}>
            <MessageCircle className="w-3.5 h-3.5" />
          </a>
        )}
        <button onClick={onProfile} title="Profil öffnen"
                className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300" data-testid={`heute-profile-${d.id}`}>
          <UserCircle2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {variant === "working" && d.status && (
        <StatusBadge status={d.status} />
      )}
    </div>
  );
}

function AbsentRow({ a, type }) {
  const Icon = { urlaub: Sun, krank: Stethoscope, frei: Coffee, homeoffice: Home }[type] || UserCircle2;
  const color = { urlaub: "text-sky-400", krank: "text-rose-400", frei: "text-zinc-400", homeoffice: "text-violet-400" }[type] || "text-zinc-400";
  return (
    <div className="py-2.5 flex items-center gap-3">
      <Icon className={`w-4 h-4 ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 font-medium truncate">{a.name}</div>
        <div className="text-xs text-zinc-500">{a.department || "—"}</div>
      </div>
    </div>
  );
}
